import { UUID } from '@finxone-platform/shared/base-types';
import { $enum } from 'ts-enum-util';
import { LayoutNames, WidgetNames } from './config.type';
import { ComponentLabel, Gestures, WorkflowConfig } from './workflow.type';
import { NewWorkflowWidgetConfig, WorkflowPageConfig, WorkflowZoneDevicesConfig } from './workflow.v2.type';
export type WidgetPath = (string | number)[];

export class NotLayoutWidgetErr extends Error {}

export function isLayoutWidget(label: ComponentLabel) {
  return layoutWidgetsArray.includes(label);
}

export const layoutWidgetsArray: ComponentLabel[] = [
  WidgetNames.SECTION,
  WidgetNames.TWO_COLUMN,
  WidgetNames.TAB,
  WidgetNames.FORM_WIDGET,
  WidgetNames.GENERIC_CAROUSEL_WIDGET,
  WidgetNames.ACCORDION_WIDGET,
];

export function findWidgetPath(
  uniqueId: string | number,
  widgets: NewWorkflowWidgetConfig[],
  currentPath: WidgetPath = [],
): WidgetPath | null {
  for (const widget of widgets) {
    const newPath = [...currentPath, widget.uniqueId];

    if (widget.uniqueId.toString() === uniqueId.toString()) {
      return newPath;
    }

    if (widget.childComponents) {
      if (Array.isArray(widget.childComponents[0])) {
        // Handle nested arrays of childComponents
        for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
          const childPath = findWidgetPath(uniqueId, childArray, newPath);
          if (childPath) return childPath;
        }
      } else {
        // Handle single array of childComponents
        const childPath = findWidgetPath(
          uniqueId,
          widget.childComponents as NewWorkflowWidgetConfig[],
          newPath,
        );
        if (childPath) return childPath;
      }
    }
  }

  return null;
}

export function deleteWidget(uniqueId: string | number, widgets: NewWorkflowWidgetConfig[]): void {
  const path = findWidgetPath(uniqueId, widgets);
  if (!path) return; // Widget not found, do nothing

  const deleteFromArray = (arr: NewWorkflowWidgetConfig[], pathIndex: number): void => {
    if (pathIndex === path.length - 1) {
      const index = arr.findIndex((widget) => widget.uniqueId === path[pathIndex]);
      if (index !== -1) {
        arr.splice(index, 1);
      }
      return;
    }

    const widget = arr.find((w) => w.uniqueId === path[pathIndex]);
    if (widget && widget.childComponents) {
      if (Array.isArray(widget.childComponents[0])) {
        widget.childComponents.forEach((childArray) =>
          deleteFromArray(childArray as NewWorkflowWidgetConfig[], pathIndex + 1),
        );
      } else {
        deleteFromArray(widget.childComponents as NewWorkflowWidgetConfig[], pathIndex + 1);
      }
    }
  };

  deleteFromArray(widgets, 0);
}

export function findWidget(
  uniqueId: string | number,
  widgets: NewWorkflowWidgetConfig[],
): NewWorkflowWidgetConfig | null {
  for (const widget of widgets) {
    if (widget.uniqueId?.toString() === uniqueId?.toString()) {
      return widget;
    }

    if (widget.childComponents) {
      let result: NewWorkflowWidgetConfig | null = null;

      if (Array.isArray(widget.childComponents[0])) {
        // Handle 2D array of child components
        for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
          result = findWidget(uniqueId, childArray);
          if (result) break;
        }
      } else {
        // Handle 1D array of child components
        result = findWidget(uniqueId, widget.childComponents as NewWorkflowWidgetConfig[]);
      }
      if (result) {
        return result;
      }
    }
  }

  return null;
}

export function getFlattenedWidgetArray(pageConfig: WorkflowPageConfig) {
  // Ensure both arrays exist to avoid null/undefined issues
  pageConfig.widgets ??= [];
  pageConfig.footerWidgets ??= [];
  // Combine main widgets and footer widgets into a single array
  return [...pageConfig.widgets, ...pageConfig.footerWidgets];
}

export function getAllWidgetNames(widgets: NewWorkflowWidgetConfig[]) {
  let widgetNames = new Set<string>();
  for (const widget of widgets) {
    widgetNames.add(widget.name);

    if (widget.childComponents) {
      if (Array.isArray(widget.childComponents[0])) {
        // Handle 2D array of child components
        for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
          widgetNames = new Set([...widgetNames, ...getAllWidgetNames(childArray)]);
        }
      } else {
        // Handle 1D array of child components
        widgetNames = new Set([
          ...widgetNames,
          ...getAllWidgetNames(widget.childComponents as NewWorkflowWidgetConfig[]),
        ]);
      }
    }

    // include widget names from nestedWidgets if present
    if (widget.widgetConfiguration.nestedWidgets) {
      widgetNames = new Set([
        ...widgetNames,
        ...Object.values(widget.widgetConfiguration.nestedWidgets).map((v) => v.name),
      ]);
    }
  }

  return widgetNames;
}

export function allWidgetsInWorkflow(config: WorkflowConfig) {
  let widgetNames = new Set<string>();
  const roles = config.permissionSections.roles;
  for (const role of roles) {
    const zones = role.zones;
    for (const zone of Object.values(zones)) {
      if (zone) {
        const zoneDevicesConfig = zone.canvases.subscription['default'] as WorkflowZoneDevicesConfig;

        for (const device of ['mobile', 'desktop', 'tablet']) {
          const pages = (zoneDevicesConfig as any)[device]?.pages;

          for (const page of pages) {
            widgetNames = new Set([...widgetNames, ...getAllWidgetNames(page.widgets)]);
          }
        }
      }
    }
  }

  return widgetNames;
}

export function unusedWidgetNames(widgetNames: Set<string>) {
  const allWidgetNames = new Set($enum(WidgetNames).getValues());
  return new Set([...allWidgetNames].filter((x) => !widgetNames.has(x)));
}

export function findAndReplaceWidget(
  uniqueId: string | number,
  widgets: NewWorkflowWidgetConfig[],
  updatedWidgetConfig: NewWorkflowWidgetConfig,
): NewWorkflowWidgetConfig | null {
  for (const widget of widgets) {
    if (widget.uniqueId.toString() === uniqueId?.toString()) {
      Object.assign(widget, updatedWidgetConfig); // Modify existing instance
      return widget;
    }

    if (widget.childComponents) {
      let result: NewWorkflowWidgetConfig | null = null;

      if (Array.isArray(widget.childComponents[0])) {
        // Handle 2D array of child components
        for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
          result = findAndReplaceWidget(uniqueId, childArray, updatedWidgetConfig);
          if (result) return result;
        }
      } else {
        // Handle 1D array of child components
        result = findAndReplaceWidget(
          uniqueId,
          widget.childComponents as NewWorkflowWidgetConfig[],
          updatedWidgetConfig,
        );
      }

      if (result) {
        return result;
      }
    }
  }

  return null;
}

export const TOP_LEVEL_ID = 'TOP_LEVEL';

export function findWidgetAndParent(
  uniqueId: string | number,
  widgets: NewWorkflowWidgetConfig[],
  parent: NewWorkflowWidgetConfig[] | null = null,
): [NewWorkflowWidgetConfig | null, NewWorkflowWidgetConfig[] | null] {
  for (const widget of widgets) {
    if (widget.uniqueId === uniqueId) {
      return [widget, parent || widgets];
    }

    if (widget.childComponents) {
      let result: [NewWorkflowWidgetConfig | null, NewWorkflowWidgetConfig[] | null] = [null, null];

      if (Array.isArray(widget.childComponents[0])) {
        // Handle 2D array of child components
        for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
          result = findWidgetAndParent(uniqueId, childArray, childArray);
          if (result[0]) return result;
        }
      } else {
        // Handle 1D array of child components
        result = findWidgetAndParent(
          uniqueId,
          widget.childComponents as NewWorkflowWidgetConfig[],
          widget.childComponents as NewWorkflowWidgetConfig[],
        );
        if (result[0]) return result;
      }
    }
  }

  return [null, null];
}

export function addWidget(
  widget: NewWorkflowWidgetConfig,
  targetParentId: string | number | typeof TOP_LEVEL_ID,
  widgets: NewWorkflowWidgetConfig[],
  targetIndex?: number,
  target2dChildComponentIndex?: number,
  retainUniqueId?: boolean,
): NewWorkflowWidgetConfig[] {
  let widgetConfig = JSON.parse(JSON.stringify(widget));
  if (!retainUniqueId && widgetConfig.uniqueId !== widgetConfig.name) {
    widgetConfig = {
      ...widgetConfig,
      uniqueId: new UUID().toString(),
    };
  }
  // If moving to top level
  if (
    targetParentId === TOP_LEVEL_ID ||
    targetParentId === LayoutNames.FOOTER_LAYOUT ||
    targetParentId === LayoutNames.PAGE_CONTENT
  ) {
    const widgetIndex = targetIndex ?? widgets.findIndex((v) => v.name === widgetConfig.name);
    if (widgetIndex !== undefined && widgetIndex >= 0 && widgetIndex <= widgets.length) {
      widgets.splice(widgetIndex, 0, widget);
    } else {
      widgets.push(widget);
    }
    return widgets;
  }

  // Find the target parent
  const targetParent = findWidget(targetParentId, widgets);

  if (!targetParent) {
    console.error('Target parent not found');
    return widgets;
  }
  if (targetParentId !== TOP_LEVEL_ID) {
    if (!isLayoutWidget((targetParent?.name ?? '') as ComponentLabel)) {
      throw new NotLayoutWidgetErr();
    }
  }
  // Ensure childComponents exists
  if (!targetParent.childComponents) {
    targetParent.childComponents = [];
  }

  // Handle 2D array of childComponents
  if (Array.isArray(targetParent.childComponents[0])) {
    const targetArray =
      target2dChildComponentIndex !== undefined &&
      target2dChildComponentIndex < targetParent.childComponents.length
        ? (targetParent.childComponents[target2dChildComponentIndex] as NewWorkflowWidgetConfig[])
        : (targetParent.childComponents[0] as NewWorkflowWidgetConfig[]);

    if (targetIndex !== undefined && targetIndex >= 0 && targetIndex <= targetArray.length) {
      targetArray.splice(targetIndex, 0, widgetConfig);
    } else {
      targetArray.push(widgetConfig);
    }
  }
  // Handle 1D array of childComponents
  else {
    const widgetIndex = targetIndex ?? targetParent.childComponents.findIndex((v) => v === widgetConfig);
    if (widgetIndex !== undefined && widgetIndex >= 0 && widgetIndex <= targetParent.childComponents.length) {
      targetParent.childComponents.splice(widgetIndex, 0, widgetConfig);
    } else {
      (targetParent.childComponents as NewWorkflowWidgetConfig[]).push(widgetConfig);
    }
  }

  updateFormChildComponentWithParentFormId(widgets);
  return widgets;
}

/**
 * Returns the widget array for a given page based on layout type or widget ID.
 *
 * Behavior:
 * 1. If `page` is undefined, returns an empty array.
 * 2. If `widgetLayoutType` is provided:
 *    - Returns `page.footerWidgets` if layout type is `FOOTER_LAYOUT`.
 *    - Returns `page.widgets` for any other layout type.
 * 3. If `widgetLayoutType` is undefined and `widgetUniqueId` is provided:
 *    - Searches `widgets` and `footerWidgets` arrays for a widget with the given ID.
 *    - Returns the array that contains the widget, if found.
 * 4. If `widgetLayoutType` is undefined and no `widgetUniqueId` is provided defaults to `page.widgets`
 * @param page - The workflow page configuration object.
 * @param widgetLayoutType - Optional layout type to specify which widget array to return.
 * @param widgetUniqueId - Optional widget ID to locate the array containing a specific widget.
 * @returns An array of `NewWorkflowWidgetConfig` objects for the specified layout or widget.
 */
export function getWidgetsArrayByLayoutTypeOrWidgetId(
  page: WorkflowPageConfig | undefined,
  widgetLayoutType?: LayoutNames | string,
  widgetUniqueId?: string | number,
): NewWorkflowWidgetConfig[] {
  if (!page) return [];

  // If a specific layout type is provided, use that array directly
  if (widgetLayoutType) {
    return widgetLayoutType === LayoutNames.FOOTER_LAYOUT ? page?.footerWidgets || [] : page.widgets || [];
  }

  // Arrays we want to inspect, with clear identifiers
  const widgetCollections: { key: 'widgets' | 'footerWidgets'; items: NewWorkflowWidgetConfig[] }[] = [
    { key: 'widgets', items: page.widgets || [] },
    { key: 'footerWidgets', items: page?.footerWidgets || [] },
  ];

  // Find which collection contains the widgetId
  for (const { items } of widgetCollections) {
    if (widgetUniqueId && findWidget(widgetUniqueId, items)) {
      return items;
    }
  }

  return page?.widgets ?? [];
}

// Widgets that should always get a parentForm assigned
const widgetsRequiringParentForm = new Set([
  WidgetNames.FORM_NAME_WIDGET,
  WidgetNames.FORM_BIRTHDAY_WIDGET,
  WidgetNames.ONBOARDING_BUTTON,
  WidgetNames.FORM_ADDRESS_WIDGET,
  WidgetNames.FORM_ADDRESS_HISTORY_WIDGET,
  WidgetNames.FORM_PASSWORD_WIDGET,
  WidgetNames.ACCOUNTS_CONTEXT_DROPDOWN,
  WidgetNames.COMPANIES_HOUSE_SEARCH_WIDGET,
]);

// Traverse all widgets (recursively) and assign formID to children
function updateFormChildComponentWithParentFormId(widgets: NewWorkflowWidgetConfig[]) {
  if (!widgets || widgets.length === 0) return;

  widgets.forEach((widget) => {
    if (widget.name === WidgetNames.FORM_WIDGET && widget.childComponents) {
      const formId = widget.widgetConfiguration?.attributes?.widgetProperties?.formsConfig?.formID ?? null;

      if (formId) {
        const applyParentId = (children: NewWorkflowWidgetConfig[]) => {
          getChildrenArray(children).forEach((child) => {
            // Add parentForm Id  if the child widget is needed
            if (
              widgetsRequiringParentForm.has(child.name as WidgetNames) &&
              child.widgetConfiguration?.attributes?.widgetProperties
            ) {
              child.widgetConfiguration.attributes.widgetProperties['parentForm'] = formId;
            }
            // Recurse deeper for more nested children
            if (child.childComponents?.length) {
              applyParentId(getChildrenArray(child.childComponents));
            }
          });
        };

        applyParentId(getChildrenArray(widget.childComponents));
      }
    }

    // Recurse to find nested form_widgets
    if (widget.childComponents?.length) {
      updateFormChildComponentWithParentFormId(getChildrenArray(widget.childComponents));
    }
  });
}

// Flatten childComponents
function getChildrenArray(
  childComponents: NewWorkflowWidgetConfig[] | NewWorkflowWidgetConfig[][],
): NewWorkflowWidgetConfig[] {
  return Array.isArray(childComponents[-1])
    ? (childComponents as NewWorkflowWidgetConfig[][]).flat()
    : (childComponents as NewWorkflowWidgetConfig[]);
}

export function moveWidget(
  widgetId: string | number,
  targetParentId: string | number | typeof TOP_LEVEL_ID,
  widgets: NewWorkflowWidgetConfig[],
  targetIndex?: number,
  target2dChildComponentIndex?: number,
  removeFromArray?: NewWorkflowWidgetConfig[],
): NewWorkflowWidgetConfig[] {
  // Determine correct source array to search and delete from
  const sourceArray = removeFromArray ?? widgets;

  // Find the widget to move
  const widgetToMove = findWidget(widgetId, sourceArray);
  if (!widgetToMove) {
    console.error('Widget to move not found');
    return widgets;
  }

  deleteWidget(widgetId, sourceArray);
  return addWidget(widgetToMove, targetParentId, widgets, targetIndex, target2dChildComponentIndex, true);
}

export function flattenWorkflowWidgets(widgets: NewWorkflowWidgetConfig[]): NewWorkflowWidgetConfig[] {
  const flattened: NewWorkflowWidgetConfig[] = [];

  function flatten(item: NewWorkflowWidgetConfig | NewWorkflowWidgetConfig[]) {
    if (Array.isArray(item)) {
      item.forEach(flatten);
    } else {
      flattened.push(item);
      if (item.childComponents) {
        if (Array.isArray(item.childComponents[0])) {
          (item.childComponents as NewWorkflowWidgetConfig[][]).forEach(flatten);
        } else {
          flatten(item.childComponents as NewWorkflowWidgetConfig[]);
        }
      }
    }
  }

  flatten(widgets);
  return flattened;
}

// Recursively update uniqueIds for all nested childComponents
export function updateChildComponentUniqueIds(
  childComponents: NewWorkflowWidgetConfig[][] | NewWorkflowWidgetConfig[] | undefined,
) {
  if (!childComponents) return;
  if (Array.isArray(childComponents)) {
    childComponents.forEach((child) => {
      if (Array.isArray(child)) {
        updateChildComponentUniqueIds(child);
      } else if (child && typeof child === 'object') {
        child.uniqueId = new UUID().toString();
        // Recursively update further nested childComponents
        if (child.childComponents) {
          updateChildComponentUniqueIds(child.childComponents);
        }
      }
    });
  }
}

// Types for tracking widget incidences
interface WidgetLocation {
  role: string;
  zone: string;
  page: string;
  device: string;
  path?: string; // Optional path for nested widgets
}

interface WidgetIncidence {
  name: string;
  count: number;
  locations: WidgetLocation[];
}

// Map to store widget names and their locations
type WidgetIncidenceMap = Map<string, WidgetLocation[]>;

export function getAllWidgetNamesWithContext(
  widgets: NewWorkflowWidgetConfig[],
  context: Omit<WidgetLocation, 'path'>,
  parentPath = '',
): WidgetIncidenceMap {
  const widgetMap: WidgetIncidenceMap = new Map();

  for (const widget of widgets) {
    const currentPath = parentPath ? `${parentPath} > ${widget.name}` : widget.name;

    // Add current widget with its location
    const locations = widgetMap.get(widget.name) || [];
    locations.push({ ...context, path: currentPath });
    widgetMap.set(widget.name, locations);

    // Process child components
    if (widget.childComponents) {
      const childMaps: WidgetIncidenceMap[] = [];

      if (Array.isArray(widget.childComponents[0])) {
        // Handle 2D array of child components
        for (const childArray of widget.childComponents as NewWorkflowWidgetConfig[][]) {
          childMaps.push(getAllWidgetNamesWithContext(childArray, context, currentPath));
        }
      } else {
        // Handle 1D array of child components
        childMaps.push(
          getAllWidgetNamesWithContext(
            widget.childComponents as NewWorkflowWidgetConfig[],
            context,
            currentPath,
          ),
        );
      }

      // Merge child maps into main map
      for (const childMap of childMaps) {
        childMap.forEach((childLocations, childName) => {
          const existingLocations = widgetMap.get(childName) || [];
          widgetMap.set(childName, [...existingLocations, ...childLocations]);
        });
      }
    }

    // Process nested widgets
    if (widget.widgetConfiguration.nestedWidgets) {
      Object.values(widget.widgetConfiguration.nestedWidgets).forEach((nestedWidget) => {
        const nestedPath = `${currentPath} > [nested] ${nestedWidget.name}`;
        const locations = widgetMap.get(nestedWidget.name) || [];
        locations.push({ ...context, path: nestedPath });
        widgetMap.set(nestedWidget.name, locations);
      });
    }
  }

  return widgetMap;
}

export function analyzeWidgetIncidences(config: WorkflowConfig): {
  allWidgets: WidgetIncidence[];
  lowIncidenceWidgets: WidgetIncidence[];
  summary: {
    totalUniqueWidgets: number;
    totalWidgetInstances: number;
    widgetsWithLessThan10: number;
  };
} {
  const globalWidgetMap: WidgetIncidenceMap = new Map();
  const roles = config.permissionSections.roles;

  // Collect all widget incidences
  for (const role of roles) {
    const zones = role.zones;

    for (const [zoneName, zone] of Object.entries(zones)) {
      if (zone) {
        const zoneDevicesConfig = zone.canvases.subscription['default'] as WorkflowZoneDevicesConfig;

        for (const device of ['mobile', 'desktop', 'tablet'] as const) {
          const pages = (zoneDevicesConfig as any)[device]?.pages || [];

          for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
            const page = pages[pageIndex];
            const context: Omit<WidgetLocation, 'path'> = {
              role: role.name || `Role ${roles.indexOf(role)}`,
              zone: zoneName,
              page: page.name || `Page ${pageIndex + 1}`,
              device,
            };

            const pageWidgetMap = getAllWidgetNamesWithContext(page.widgets, context);

            // Merge page widget map into global map
            pageWidgetMap.forEach((locations, widgetName) => {
              const existingLocations = globalWidgetMap.get(widgetName) || [];
              globalWidgetMap.set(widgetName, [...existingLocations, ...locations]);
            });
          }
        }
      }
    }
  }

  // Convert map to array of WidgetIncidence objects
  const allWidgets: WidgetIncidence[] = Array.from(globalWidgetMap.entries()).map(([name, locations]) => ({
    name,
    count: locations.length,
    locations,
  }));

  // Sort by count (descending) for better readability
  allWidgets.sort((a, b) => b.count - a.count);

  // Filter widgets with less than 10 incidences
  const lowIncidenceWidgets = allWidgets.filter((widget) => widget.count < 5);

  // Calculate summary statistics
  const totalWidgetInstances = allWidgets.reduce((sum, widget) => sum + widget.count, 0);

  return {
    allWidgets,
    lowIncidenceWidgets,
    summary: {
      totalUniqueWidgets: allWidgets.length,
      totalWidgetInstances,
      widgetsWithLessThan10: lowIncidenceWidgets.length,
    },
  };
}

// Return true when any known gesture property is explicitly enabled.
export function isGesturesEnabled(gestures: Gestures): boolean {
  if (!gestures || typeof gestures !== 'object') return false;
  return gestures.swipeEnabled === true || gestures.dragEnabled === true || gestures.panEnabled === true;
}
